import React from "react";
import { useState } from "react";
import { Button, Checkbox, InputAdornment, useTheme } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setCredentials, setUser, setUserType } from "../store/auth/authSlice";
import {
  useLoginMutation,
  useLoginWithGoogleMutation,
} from "../store/auth/authApiSlice";
import secureLocalStorage from "react-secure-storage";
import { Box } from "@mui/material";
import GoogleIcons from "../assets/images/google_icon.svg";
import { useLazyGetUserProfileQuery } from "../store/users/usersApiSlice";
import { useGoogleLogin } from "@react-oauth/google";
import CustomButton from "../theme/style-components/CustomButton";
import CustomInput from "../theme/style-components/CustomInput";
import * as yup from "yup";
import { useFormik } from "formik";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { encryptFn } from "../shared/helperFunction";

const SignIn = ({ handleForget }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  const [getUserProfile] = useLazyGetUserProfileQuery();
  const [isRemember, setIsRemember] = useState(false);
  const [passwordShow, setPasswordShow] = useState(true);
  const [loginWithGoogle, { isLoading: isGoogleLoading }] =
    useLoginWithGoogleMutation();
  const theme = useTheme();

  const validationSchema = yup.object({
    email: yup
      .string()
      .email("Please enter a valid email")
      .required("Please enter a email"),
    password: yup.string().required("Please enter a password"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const { email, password } = values;
      try {
        const userData = await login({ email, password }).unwrap();
        localStorage.setItem("refreshToken", encryptFn(userData.refresh));
        localStorage.setItem("accessToken", encryptFn(userData.access));
        localStorage.setItem("email", email);
        localStorage.setItem("isAuthenticated", true);
        secureLocalStorage.setItem("password", password);
        localStorage.setItem(
          "userType",
          userData.is_superuser ? "ADMIN" : "EMPLOYEE"
        );
        dispatch(setUserType(userData.is_superuser ? "ADMIN" : "EMPLOYEE"));
        dispatch(setCredentials({ ...userData, email }));
        dispatch(
          setUser({
            email: email,
            first_name: userData.first_name,
            last_name: userData.last_name,
            dp: userData.dp,
          })
        );
        navigate("/employee", { replace: true });
        try {
          await getUserProfile()
            .unwrap()
            .then((res) => {
              if (res) {
                dispatch(setCredentials({ ...userData, email }));
                localStorage.setItem(
                  "userType",
                  res.data.is_superuser ? "ADMIN" : "EMPLOYEE"
                );
                dispatch(
                  setUserType(res.data.is_superuser ? "ADMIN" : "EMPLOYEE")
                );
                dispatch(setUser(res.data));
              }

              if (res.data.is_superuser) {
                navigate("/employee", { replace: true });
              } else {
                navigate("/org_chart", { replace: true });
              }
            });
        } catch (err) {
          console.log(err);
        }
        toast.success("You are logged in!");
      } catch (error) {
        if (error) {
          toast.error("Wrong username or password");
        }
      }
    },
  });

  const handleChangeRemember = (event) => {
    setIsRemember(event.target.checked);
  };

  const handleLoginWithGoogle = async (token) => {
    try {
      const userProfile = await loginWithGoogle(token)
        .unwrap()
        .then((res) => {
          if (res) {
            navigate("/employee");
          }
        });
      toast.success("Login successful!");
    } catch (error) {
      console.log("Something went wrong!");
      if (error?.data) {
        toast.error(error.data.non_field_errors[0]);
      }
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    scope: "https://www.googleapis.com/auth/userinfo.email",
    onSuccess: (tokenResponse) => {
      if (tokenResponse?.access_token) {
        handleLoginWithGoogle(tokenResponse?.access_token);
      }
    },
    onError: () => {
      toast.error("Login Failed");
    },
  });

  return (
    <>
      <Box>
        <Box sx={{ marginBottom: "15px" }}>
          <CustomInput
            id="email"
            variant="outlined"
            margin="normal"
            placeholder="Email"
            name="email"
            label="Email"
            required
            fullWidth
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Box>
        <Box sx={{ marginBottom: "15px" }}>
          <CustomInput
            id="password"
            variant="outlined"
            margin="normal"
            placeholder="Password"
            label="Password"
            name="password"
            type={!passwordShow ? "text" : "password"}
            required
            fullWidth
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    cursor: "pointer",
                    "& svg": {
                      fill: theme.palette.grey["A400"],
                    },
                  }}
                >
                  {passwordShow ? (
                    <VisibilityOffIcon
                      onClick={() => setPasswordShow(!passwordShow)}
                    />
                  ) : (
                    <VisibilityIcon
                      onClick={() => setPasswordShow(!passwordShow)}
                    />
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Checkbox
            sx={{
              color: `${theme.palette.mode === "dark" ? "#FFF" : "#000000"}`,
              "&.Mui-checked": {
                color: "#5bbba9",
              },
            }}
            checked={isRemember}
            onChange={handleChangeRemember}
            inputProps={{ "aria-label": "controlled" }}
          />
          <Typography
            sx={{
              color: `${theme.palette.mode === "dark" ? "#FFF" : "#000000"}`,
              fontSize: { xs: "12px", sm: "14px", md: "14px" },
              lineHeight: "20px",
              fontWeight: "400",
            }}
          >
            Remember me?
          </Typography>
        </Box>
        <Box
          sx={{
            textAlign: "right",
            color: `${theme.palette.mode === "dark" ? "#FFF" : "#000000"}`,
            fontSize: { xs: "12px", sm: "14px", md: "14px" },
            lineHeight: "20px",
            fontWeight: "400",
            cursor: "pointer",
          }}
          onClick={() => handleForget()}
        >
          Forgot Password?
        </Box>
      </Box>
      <Box sx={{ marginTop: "10px" }}>
        <CustomButton
          fullWidth
          disabled={isLoading || !formik.isValid}
          size="large"
          color="primary"
          onClick={formik.handleSubmit}
          buttonName={isLoading ? "Signing..." : "Sign In"}
        />
      </Box>
      <Box
        sx={{
          marginTop: "10px",
          textAlign: "center",
          position: "relative",
          zIndex: "1",
          "&:after": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: "0",
            width: "100%",
            height: "1px",
            backgroundColor: `${theme.palette.border.primary} !important`,
            transform: "translateY(-50%)",
            zIndex: "-1",
          },
        }}
      >
        <Typography
          sx={{
            color: "#5bbba9",
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: "400",
            textAlign: "center",
            display: "inline-block",
            backgroundColor: theme.palette.background.paper,
            padding: "0 50px",
          }}
        >
          or
        </Typography>
      </Box>
      <Box sx={{ marginTop: "10px" }}>
        <Button
          sx={{
            border: `1px solid ${theme.palette.border.primary}`,
            color: "#000000",
            backgroundColor: theme.palette.background.default,
            textTransform: "none",
            padding: "13px",
            borderRadius: "10px",
            fontWeight: "600",
            gap: "10px",
          }}
          onClick={() => handleGoogleLogin()}
          fullWidth
          disabled={isGoogleLoading}
        >
          <img src={GoogleIcons} alt="profile-img" />
          Sign In with Google
        </Button>
      </Box>
    </>
  );
};

export default SignIn;
